// Central registry of long-form articles (op-ed style essays).
// Used by /articles index, sitemap, and cross-links from /briefings.
//
// `readingMinutes` is auto-computed at build time by the
// `article-reading-times` Vite plugin (see vite.config.ts), which scans the
// article JSX in src/pages/articles/* and counts words. The hardcoded
// `readingMinutes` below acts as a fallback only.

import { readingTimes } from "virtual:article-reading-times";

export interface ArticleMeta {
  slug: string;
  path: string;
  title: string;
  description: string;
  datePublished: string; // ISO date
  category: string;
  readingMinutes: number;
  showOnHomepage?: boolean;
}

interface ArticleSeed extends Omit<ArticleMeta, "readingMinutes"> {
  readingMinutes?: number; // optional fallback
}

const seeds: ArticleSeed[] = [
  {
    slug: "i-dont-speak-medicalese",
    path: "/articles/i-dont-speak-medicalese",
    title: "I Don't Speak Medicalese",
    description: "I run a coalition of clinicians, and I am not one.",
    datePublished: "2026-10-23",
    category: "Leadership",
    showOnHomepage: false,
  },
  {
    slug: "geography-not-billing-code",
    path: "/articles/geography-not-billing-code",
    title: "Geography Is Not a Billing Code",
    description:
      "Neighbor-island patients pay more for the same care than Oʻahu residents with identical insurance. It is pricing design, not geography.",
    datePublished: "2026-05-27",
    category: "Policy & Strategy",
  },
  {
    slug: "644-doctors-short",
    path: "/articles/644-doctors-short",
    title: "You Are 644 Doctors Short. The Number Is Getting Worse.",
    description:
      "Hawaiʻi has 12,688 licensed physicians but only 3,044 practicing FTE — a 644-physician shortfall driven by retention, not recruitment.",
    datePublished: "2026-05-26",
    category: "Workforce",
  },
  {
    slug: "directory-is-lying",
    path: "/articles/directory-is-lying",
    title: "The Directory Is Lying to You",
    description:
      "What ghost networks actually cost Hawaiʻi patients and why no one is held accountable.",
    datePublished: "2026-05-25",
    category: "Network Adequacy",
  },
  {
    slug: "776-physician-gap",
    path: "/articles/776-physician-gap",
    title: "The 776-Physician Gap",
    description:
      "Why Hawaiʻi's doctor shortage is a federal math problem — and what a state-of-the-union fix actually requires.",
    datePublished: "2026-04-19",
    category: "Workforce",
  },
  {
    slug: "hidden-tax-hawaii-medicine",
    path: "/articles/hidden-tax-hawaii-medicine",
    title: "The Hidden Tax on Hawaiʻi Medicine",
    description:
      "How the GET on medical services quietly drains primary care capacity statewide — and the structural reform on the table.",
    datePublished: "2026-04-12",
    category: "Policy & Strategy",
  },
  {
    slug: "network-adequacy-theater",
    path: "/articles/network-adequacy-theater",
    title: "Network Adequacy Theater",
    description:
      "Hawaiʻi's network adequacy rules are paperwork, not access. Here's what the data actually shows.",
    datePublished: "2026-04-05",
    category: "Policy & Strategy",
  },
  {
    slug: "neighbor-island-surcharge",
    path: "/articles/neighbor-island-surcharge",
    title: "The Neighbor-Island Surcharge",
    description:
      "Geography is not a billing code. The hidden cost of treating Hawaiʻi like a single county.",
    datePublished: "2026-03-28",
    category: "Rural Health",
  },
  {
    slug: "2-3-billion-question",
    path: "/articles/2-3-billion-question",
    title: "The $2.3 Billion Question",
    description:
      "Where the money already goes — and why the system keeps starving the front line.",
    datePublished: "2026-03-21",
    category: "Funding & Capital",
  },
  {
    slug: "merger-math",
    path: "/articles/merger-math",
    title: "Merger Math",
    description:
      "The arithmetic behind the latest health-system consolidation and what it means for patients across the islands.",
    datePublished: "2026-03-14",
    category: "Healthcare Systems",
  },
];

export const articles: ArticleMeta[] = seeds.map((s) => ({
  ...s,
  readingMinutes: readingTimes[s.slug] ?? s.readingMinutes ?? 5,
}));
